import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VideoService from '../../../../../../service/VideoService';
import { CustomButton } from '../../../../../../components/Button/Button';
import styles from './Logos.module.scss';
import Icon from '../../../../../../components/Icon/Icon';
import cx from 'classnames';
import arrayMove from '../../../../../../utils/arrayUtil';
import SortableItemsContainer from '../../../../../../components/Sortable/SortableItemsContainer';
import SortableItem from '../../../../../../components/Sortable/SortableItem';
import ScrollContainer from '../../../../../../components/ScrollContainer/ScrollContainer';
import SortableItemHandle from '../../../../../../components/Sortable/SortableItemHandle';
import { useTranslation } from 'react-i18next';
import Spinner from '../../../../../../../common/components/Spinner';
import UploadLogoModal from '../UploadLogo/UploadLogoModal';
import { ToastNotification } from '../../../../../../components/Toast/Toast';
import useWindowSize from '../../../../../../hooks/useWindowSize';
import { useUserContext } from '../../../../../../context/UserContext';

const Logos = ({ handleOpenEditor, companyId }) => {
  const [logosList, setLogosList] = useState([]);
  const [selectedLogoId, setSelectedLogoId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { height } = useWindowSize();

  const { updateUserAppConfig } = useUserContext();

  const logoListRef = useRef(null);

  const logoListStyles = useMemo(() => {
    if (!logoListRef.current) {
      return {};
    }

    const { top } = logoListRef.current.getBoundingClientRect();
    const maxHeight = window.innerHeight - top - 64;
    return { maxHeight };
  }, [height, logoListRef.current]);

  const { t } = useTranslation();

  const handleSortStart = useCallback(
    ({ index }) => {
      const { id } = logosList.find((logo, logoIndex) => index === logoIndex) || {};
      setSelectedLogoId(id);
    },
    [logosList]
  );

  const handleSortEnd = useCallback(
    async ({ oldIndex, newIndex }) => {
      if (oldIndex !== newIndex) {
        try {
          setIsLoading(true);
          const movedList = arrayMove(logosList, oldIndex, newIndex);
          setLogosList(movedList);
          const payload = movedList.map(({ id }, index) => ({ id, sort: index }));
          const result = await VideoService.updateLogos(payload, companyId);

          const sortedList = result.sort((a, b) => a.sort - b.sort);
          setLogosList(sortedList);
          updateUserAppConfig();

          ToastNotification({
            message: t('company.settings.logos.sort.success'),
            iconName: 'success',
          });
        } catch (err) {
          console.log(err);
          ToastNotification({
            message: t('company.settings.logos.sort.error'),
          });
        }

        setIsLoading(false);
      }

      setSelectedLogoId(null);
    },
    [logosList, selectedLogoId]
  );

  const getLogosList = async () => {
    try {
      setIsLoading(true);
      const result = await VideoService.getLogos(companyId);
      if (result.data) {
        const sortedList = result.data.sort((a, b) => a.sort - b.sort);
        setLogosList(sortedList);
      }
    } catch (err) {
      console.log(err);
    }

    setIsLoading(false);
  };

  const handleDeleteLogo = async logoId => {
    try {
      const isConfirm = confirm(t('company.settings.logos.delete.confirmation'));

      if (!isConfirm) {
        return;
      }
      setIsLoading(true);
      await VideoService.deleteLogo(logoId, companyId);

      updateUserAppConfig();

      ToastNotification({
        message: t('company.settings.logos.delete.success'),
        iconName: 'success',
      });

      await getLogosList();
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: t('company.settings.logos.delete.error'),
      });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    getLogosList();
  }, [companyId]);

  return (
    <div className={styles.mainContainer}>
      <div className={styles.headerContainer}>
        <span className={styles.logoHeader}>
          {t('company.settings.logos.all_logos')} ({logosList.length})
        </span>
        <CustomButton
          label={t('company.settings.logos.new_logo')}
          variant="primary"
          onClick={() => setIsUploadModalOpen(true)}
          disabled={isLoading}
          className={styles.newLogoBtn}
        />
      </div>
      <div ref={logoListRef} style={{ ...logoListStyles }} className={styles.logosListContainer}>
        <SortableItemsContainer
          axis="y"
          useDragHandle={true}
          helperClass={styles.sortable}
          updateBeforeSortStart={handleSortStart}
          onSortEnd={handleSortEnd}
          onSortOver={() => setSelectedLogoId(null)}
        >
          <ScrollContainer className={cx(styles.scrollContainer, { [styles.scrollContainerDisabled]: isLoading })} vertical={true}>
            {logosList.map((logo, index) => (
              <SortableItem key={logo.id} index={index} itemId={logo.id} tabIndex={0} disabled={isLoading}>
                <div key={logo?.id} className={cx(styles.logoItem, { [styles.logoItemActive]: logo?.id === selectedLogoId })}>
                  <SortableItemHandle>
                    <div className={cx(styles.logoDragContainer, { [styles.logoDragActive]: logo?.id === selectedLogoId })}>
                      <Icon src="dragDots" className={styles.dragIcon} />
                      <div className={styles.logoImgContainer}>
                        <img className={styles.logoImg} src={logo?.url} alt={t('logo_chooser.header')} />
                      </div>
                    </div>
                  </SortableItemHandle>
                  <div className={styles.logoActionContainer}>
                    <CustomButton variant="link" onClick={() => handleOpenEditor(logo?.id)} className={styles.actionBtn} ariaLabel={t('accessibility.pencil')}>
                      <Icon src="pencil" className={styles.actionIcon} />
                    </CustomButton>
                    <CustomButton variant="link" onClick={() => handleDeleteLogo(logo?.id)} className={styles.binActionBtn} ariaLabel={t('accessibility.bin')}>
                      <Icon src="bin_red" className={styles.binActionIcon} />
                    </CustomButton>
                  </div>
                </div>
              </SortableItem>
            ))}
          </ScrollContainer>
          {isLoading && <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />}
        </SortableItemsContainer>
      </div>

      <UploadLogoModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleOpenEditor}
        companyId={companyId}
      />
    </div>
  );
};

export default Logos;
